/**
 * Background field of dim scattered glyphs so the void never reads as
 * flat black. Populates a container with N sprites at fixed random
 * positions across a screen-sized rectangle. Regenerated only if the
 * viewport changes.
 *
 * Deterministic RNG — same seed → same field, so the noise doesn't
 * shimmer between frames.
 */

import { Container, Sprite, Texture } from 'pixi.js';
import type { GlyphAtlas } from './atlas';
import { AMBIENT_NOISE_ALPHA, AMBIENT_NOISE_DENSITY, AMBIENT_NOISE_GLYPHS, COLOR_AMBIENT_NOISE } from './style';

/** Small deterministic PRNG (mulberry32) for stable noise placement. */
function mulberry32(seed: number): () => number {
  let a = seed;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export class AmbientNoiseLayer {
  readonly container: Container = new Container();
  private cellPx = 20;
  private lastW = 0;
  private lastH = 0;

  setCellSize(px: number): void {
    if (this.cellPx === px) return;
    this.cellPx = px;
    this.rebuild(this.lastW, this.lastH, this.currentAtlas);
  }

  private currentAtlas: GlyphAtlas | null = null;

  /** (Re)build the noise field for the given viewport size. */
  build(atlas: GlyphAtlas, viewportW: number, viewportH: number): void {
    this.currentAtlas = atlas;
    this.rebuild(viewportW, viewportH, atlas);
  }

  private rebuild(w: number, h: number, atlas: GlyphAtlas | null): void {
    if (!atlas || w <= 0 || h <= 0) return;
    this.lastW = w;
    this.lastH = h;
    this.container.removeChildren();
    const cols = Math.ceil(w / this.cellPx) + 2;
    const rows = Math.ceil(h / this.cellPx) + 2;
    const rng = mulberry32(0x9c3a7f + cols * 31 + rows);
    const scale = this.cellPx / atlas.cellH;
    for (let ry = 0; ry < rows; ry++) {
      for (let rx = 0; rx < cols; rx++) {
        if (rng() > AMBIENT_NOISE_DENSITY) continue;
        const glyphIdx = Math.floor(rng() * AMBIENT_NOISE_GLYPHS.length);
        const glyph = AMBIENT_NOISE_GLYPHS[glyphIdx] ?? '.';
        const tex = atlas.get(glyph) ?? Texture.EMPTY;
        const s = new Sprite(tex);
        s.anchor.set(0.5, 0.5);
        s.scale.set(scale);
        // Slight per-glyph position jitter within the cell.
        s.x = (rx - 1) * this.cellPx + (rng() * 0.6 + 0.2) * this.cellPx;
        s.y = (ry - 1) * this.cellPx + (rng() * 0.6 + 0.2) * this.cellPx;
        s.tint = COLOR_AMBIENT_NOISE;
        s.alpha = AMBIENT_NOISE_ALPHA * (0.5 + rng() * 0.5);
        this.container.addChild(s);
      }
    }
  }

  resize(viewportW: number, viewportH: number): void {
    if (!this.currentAtlas) return;
    if (viewportW === this.lastW && viewportH === this.lastH) return;
    this.rebuild(viewportW, viewportH, this.currentAtlas);
  }
}
